import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Plus, Copy, Check, Upload, Image as ImageIcon, Trash2, RotateCcw, Mail,
  ExternalLink, LayoutDashboard, Layers3, Images as ImagesIcon, Grid3X3, Inbox,
  FileUp, MailWarning, Clock3,
} from "lucide-react";
import { api, resolveUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import "./AdminPanel.css";

const SEG_LABELS = { TOP: "Oberteile", MIDDLE: "Mittelteile", BOTTOM: "Unterteile" };
const STATUS_OPTS = ["FREE", "RESERVED", "TATTOOED", "DISABLED"];

const NAV_ITEMS = [
  { id: "overview", label: "Übersicht", icon: LayoutDashboard },
  { id: "sets", label: "Sets", icon: Layers3 },
  { id: "assets", label: "Motive", icon: ImagesIcon },
  { id: "matrix", label: "Kombinationen", icon: Grid3X3 },
  { id: "requests", label: "Anfragen", icon: Inbox },
];

const SECTION_META = {
  overview: ["Cockpit", "Alles Wichtige auf einen Blick"],
  sets: ["Inhalte", "Sets verwalten"],
  assets: ["Inhalte", "Motive verwalten"],
  matrix: ["Status", "Kombinationen verwalten"],
  requests: ["Kommunikation", "Anfragen bearbeiten"],
};

export default function AdminPanel() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const initialTab = NAV_ITEMS.some((item) => item.id === requestedTab) ? requestedTab : "overview";
  const [sets, setSets] = useState([]);
  const [activeSetId, setActiveSetId] = useState("");
  const [activeTab, setActiveTab] = useState(initialTab);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    if (ready && !user) nav("/admin/login");
  }, [ready, user, nav]);

  const loadSets = useCallback(async () => {
    const { data } = await api.get("/admin/sets");
    setSets(data);
    setActiveSetId((current) => {
      if (current && data.some((item) => item.id === current)) return current;
      return data[0]?.id || "";
    });
  }, []);

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/overview");
      setOverview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Übersicht konnte nicht geladen werden");
    }
  }, []);

  useEffect(() => {
    if (ready && user) {
      loadSets();
      loadOverview();
    }
  }, [ready, user, loadSets, loadOverview]);

  if (!ready || !user) return null;

  const currentSet = sets.find((item) => item.id === activeSetId);
  const [kicker, title] = SECTION_META[activeTab] || SECTION_META.overview;
  const needsSet = activeTab === "assets" || activeTab === "matrix";

  const openTab = (tab) => {
    setActiveTab(tab);
    if (tab === "overview") loadOverview();
  };

  return (
    <div className="admin-shell" data-testid="admin-panel-container">
      <div className="admin-layout">
        <aside className="admin-sidebar">
          <div className="admin-brand">
            <h1 className="admin-brand-title">ARTPART</h1>
            <div className="admin-brand-kicker">TattooGen · Admin</div>
          </div>

          <nav className="admin-nav" aria-label="Admin-Menü">
            {NAV_ITEMS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                className={`admin-nav-button ${activeTab === id ? "is-active" : ""}`}
                onClick={() => openTab(id)}
                data-testid={`admin-tab-${id}`}
              >
                <Icon size={17} />
                <span>{label}</span>
              </button>
            ))}
            <button type="button" className="admin-nav-button" onClick={() => nav("/admin/master-import")}>
              <FileUp size={17} />
              <span>Master-Import</span>
            </button>
          </nav>

          <div className="admin-sidebar-footer">
            <button type="button" className="admin-secondary-action" onClick={() => nav("/")}>
              <ExternalLink size={14} /> Website öffnen
            </button>
            <button
              type="button"
              className="admin-secondary-action"
              onClick={() => { logout(); nav("/admin/login"); }}
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Abmelden
            </button>
          </div>
        </aside>

        <main className="admin-main">
          <header className="admin-topline">
            <div>
              <div className="admin-page-kicker">{kicker}</div>
              <h2 className="admin-page-title">{title}</h2>
            </div>
            <div className="admin-user">{user.email}</div>
          </header>

          {needsSet && (
            <div className="admin-set-toolbar">
              <Label className="text-stone-300 text-xs shrink-0">Set bearbeiten</Label>
              <Select value={activeSetId} onValueChange={setActiveSetId}>
                <SelectTrigger data-testid="admin-set-selector" className="bg-surface border-stone-700 max-w-sm">
                  <SelectValue placeholder="Set wählen" />
                </SelectTrigger>
                <SelectContent>
                  {sets.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}{item.active ? " · öffentlich" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Tabs value={activeTab} onValueChange={openTab}>
            <TabsContent value="overview">
              <OverviewTab overview={overview} openTab={openTab} nav={nav} />
            </TabsContent>
            <TabsContent value="sets">
              <SetsTab sets={sets} reload={() => Promise.all([loadSets(), loadOverview()])} setActiveSetId={setActiveSetId} activeSetId={activeSetId} />
            </TabsContent>
            <TabsContent value="assets">{currentSet ? <AssetsTab set={currentSet} /> : <Empty />}</TabsContent>
            <TabsContent value="matrix">{currentSet ? <MatrixTab set={currentSet} /> : <Empty />}</TabsContent>
            <TabsContent value="requests"><RequestsTab onChanged={loadOverview} /></TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}

function OverviewTab({ overview, openTab, nav }) {
  const stats = [
    { label: "Offene Anfragen", value: overview?.open_requests ?? "–", icon: Clock3, alert: (overview?.open_requests || 0) > 0 },
    { label: "Reservierte Kombinationen", value: overview?.reserved ?? "–", icon: Grid3X3 },
    { label: "Offene Mailzustellung", value: overview?.mail_issues ?? "–", icon: MailWarning, alert: (overview?.mail_issues || 0) > 0 },
    { label: "Motive in allen Sets", value: overview?.assets ?? "–", icon: ImagesIcon },
  ];

  return (
    <div>
      <div className="admin-overview-grid">
        {stats.map(({ label, value, icon: Icon, alert }) => (
          <div key={label} className={`admin-stat ${alert ? "is-alert" : ""}`}>
            <div className="admin-stat-icon"><Icon size={17} /></div>
            <div className="admin-stat-value">{value}</div>
            <div className="admin-stat-label">{label}</div>
          </div>
        ))}
      </div>

      <div className="admin-overview-columns">
        <section className="admin-content-card admin-panel-section">
          <h3 className="admin-section-title">Letzte Tattoo-Anfragen</h3>
          <p className="admin-section-note">Die fünf zuletzt eingegangenen Anfragen.</p>
          <div className="admin-recent-list">
            {!overview?.recent_requests?.length ? (
              <div className="text-stone-500 text-sm py-5">Noch keine Anfragen vorhanden.</div>
            ) : overview.recent_requests.map((request) => (
              <div className="admin-recent-row" key={request.id}>
                <div className="admin-recent-number">NR. {String(request.number).padStart(3, "0")}</div>
                <div>
                  <div className="admin-recent-name">{request.name}</div>
                  <div className="admin-recent-meta">{request.set_name} · {new Date(request.created_at).toLocaleString("de-DE")}</div>
                </div>
                <div className="admin-recent-status">{request.admin_status}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="admin-content-card admin-panel-section">
          <h3 className="admin-section-title">Direktzugriff</h3>
          <p className="admin-section-note">
            {overview ? `${overview.sets} Sets · ${overview.public_sets} öffentlich · ${overview.studio_interests} Studio-Anfragen` : "Daten werden geladen …"}
          </p>
          <div className="admin-quick-actions">
            <button type="button" className="admin-quick-action" onClick={() => openTab("requests")}><Inbox size={16} /> Anfragen bearbeiten</button>
            <button type="button" className="admin-quick-action" onClick={() => openTab("assets")}><ImagesIcon size={16} /> Motive verwalten</button>
            <button type="button" className="admin-quick-action" onClick={() => nav("/admin/master-import")}><FileUp size={16} /> Masterbilder importieren</button>
            <button type="button" className="admin-quick-action" onClick={() => openTab("sets")}><Layers3 size={16} /> Sets verwalten</button>
          </div>
        </section>
      </div>
    </div>
  );
}

const Empty = () => <p className="text-stone-500 text-sm py-8 text-center">Kein Set ausgewählt.</p>;

function SetsTab({ sets, reload, setActiveSetId, activeSetId }) {
  const [form, setForm] = useState({ name: "", description: "", price_label: "" });
  const [deletingId, setDeletingId] = useState("");

  const create = async () => {
    if (!form.name) return toast.error("Name erforderlich");
    const { data } = await api.post("/admin/sets", form);
    setForm({ name: "", description: "", price_label: "" });
    setActiveSetId(data.id);
    toast.success("Set angelegt");
    reload();
  };

  const setPublic = async (id, active) => {
    await api.put(`/admin/sets/${id}`, { active });
    toast.success(active ? "Set ist jetzt öffentlich" : "Set ist jetzt privat");
    reload();
  };

  const duplicate = async (id) => {
    const { data } = await api.post(`/admin/sets/${id}/duplicate`);
    toast.success("Set dupliziert");
    setActiveSetId(data.id);
    reload();
  };

  const save = async (id, patch) => {
    await api.put(`/admin/sets/${id}`, patch);
    toast.success("Gespeichert");
    reload();
  };

  const deleteSet = async (set) => {
    const deleteAssets = window.confirm(`Set „${set.name}“ löschen?\n\nOK = Set UND zugehörige Bilder löschen\nAbbrechen = nichts löschen`);
    if (!deleteAssets) return;

    const really = window.confirm(`Wirklich „${set.name}“ inklusive aller zugehörigen Bilder löschen? Dieser Schritt kann nicht rückgängig gemacht werden.`);
    if (!really) return;

    setDeletingId(set.id);
    try {
      await api.delete(`/admin/sets/${set.id}`, { params: { delete_assets: true } });
      toast.success("Set und Bilder gelöscht");
      if (activeSetId === set.id) setActiveSetId("");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally {
      setDeletingId("");
    }
  };

  const overviewSlots = Array.from({ length: 6 }, (_, i) => sets[i] || null);

  return (
    <div className="space-y-4">
      <div className="bg-surface border border-stone-800 rounded-xl p-4">
        <div className="flex items-end justify-between gap-3 mb-3">
          <div>
            <h3 className="font-cormorant text-xl text-brass">Set-Übersicht</h3>
            <p className="text-xs text-stone-400 mt-1">Bis zu sechs Welten auf einen Blick. Öffentliche Sets erscheinen vorne als direkter Umschalter.</p>
          </div>
          <span className="font-mono-meta text-[10px] text-stone-500">{sets.filter((s) => s.active).length} öffentlich</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {overviewSlots.map((s, i) => s ? (
            <button
              key={s.id}
              type="button"
              onClick={() => setActiveSetId(s.id)}
              className={`text-left rounded-xl border p-3 transition ${activeSetId === s.id ? "border-amber-600 bg-amber-950/20" : "border-stone-800 bg-canvas"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-cormorant text-lg leading-tight">{s.name}</span>
                <span className={`font-mono-meta text-[9px] rounded-full px-2 py-0.5 border ${s.active ? "text-emerald-400 border-emerald-800" : "text-stone-500 border-stone-700"}`}>{s.active ? "ÖFFENTLICH" : "PRIVAT"}</span>
              </div>
              <div className="font-mono-meta text-[10px] text-stone-500 mt-3">SET {String(i + 1).padStart(2, "0")}</div>
            </button>
          ) : (
            <div key={`empty-${i}`} className="rounded-xl border border-dashed border-stone-800 p-3 min-h-[82px] flex items-center justify-center text-stone-600 text-xs">Freier Set-Platz</div>
          ))}
        </div>
      </div>

      <div className="bg-surface border border-stone-800 rounded-xl p-4">
        <h3 className="font-cormorant text-lg text-brass mb-2">Öffentliche Sets</h3>
        <p className="text-xs text-stone-400 mb-4">Mehrere Sets können gleichzeitig öffentlich sein. Genau diese Sets erscheinen vorne als Umschalter.</p>
        <h3 className="font-cormorant text-lg text-brass mb-3">Neues Set anlegen</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <Input data-testid="set-name-input" placeholder="Name z.B. Roboter-Bande #01" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-canvas border-stone-700" />
          <Input placeholder="Preistext z.B. 350 €" value={form.price_label} onChange={(e) => setForm({ ...form, price_label: e.target.value })} className="bg-canvas border-stone-700" />
          <Input placeholder="Beschreibung" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-canvas border-stone-700" />
        </div>
        <Button onClick={create} data-testid="create-set-button" className="cta-brass mt-3 rounded-full"><Plus size={16} className="mr-1" /> Anlegen</Button>
      </div>

      <div className="space-y-3">
        {sets.map((s) => (
          <SetRow key={s.id} s={s} setPublic={setPublic} duplicate={duplicate} save={save} deleteSet={deleteSet} deleting={deletingId === s.id} />
        ))}
      </div>
    </div>
  );
}

function SetRow({ s, setPublic, duplicate, save, deleteSet, deleting }) {
  const [edit, setEdit] = useState({ name: s.name, price_label: s.price_label || "", description: s.description || "" });
  return (
    <div className="bg-surface border border-stone-800 rounded-xl p-4" data-testid={`set-row-${s.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="font-cormorant text-lg">{s.name}</span>
          {s.active && <span className="font-mono-meta text-[10px] text-emerald-400 border border-emerald-800 rounded-full px-2 py-0.5">ÖFFENTLICH</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="border-stone-700 text-stone-300" data-testid={`publish-set-${s.id}`} onClick={() => setPublic(s.id, !s.active)}>
            <Check size={14} className="mr-1" />{s.active ? "Privat schalten" : "Öffentlich schalten"}
          </Button>
          <Button size="sm" variant="outline" className="border-stone-700 text-stone-300" onClick={() => duplicate(s.id)}><Copy size={14} className="mr-1" />Duplizieren</Button>
          <Button size="sm" variant="outline" disabled={deleting} className="border-red-900 text-red-400 hover:text-red-300 hover:bg-red-950/30" onClick={() => deleteSet(s)}>
            <Trash2 size={14} className="mr-1" />{deleting ? "Lösche …" : "Set + Bilder löschen"}
          </Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-2 mt-3">
        <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="bg-canvas border-stone-700 text-sm" />
        <Input value={edit.price_label} onChange={(e) => setEdit({ ...edit, price_label: e.target.value })} className="bg-canvas border-stone-700 text-sm" placeholder="Preistext" />
        <div className="flex gap-2">
          <Input value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className="bg-canvas border-stone-700 text-sm" placeholder="Beschreibung" />
          <Button size="sm" className="cta-brass rounded-full shrink-0" onClick={() => save(s.id, edit)}>Speichern</Button>
        </div>
      </div>
    </div>
  );
}

function AssetsTab({ set }) {
  const [assets, setAssets] = useState({ TOP: [], MIDDLE: [], BOTTOM: [] });
  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/sets/${set.id}/assets`);
    setAssets(data);
  }, [set.id]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      {["TOP", "MIDDLE", "BOTTOM"].map((seg) => (
        <div key={seg}>
          <h3 className="font-cormorant text-lg text-brass mb-3">{SEG_LABELS[seg]}</h3>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            {Array.from({ length: 10 }).map((_, i) => {
              const slot = i + 1;
              const a = assets[seg].find((x) => x.slot_number === slot);
              return <AssetSlot key={slot} setId={set.id} seg={seg} slot={slot} asset={a} onDone={load} />;
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function AssetSlot({ setId, seg, slot, asset, onDone }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("segment_type", seg);
    fd.append("slot_number", slot);
    fd.append("name", asset?.name || "");
    fd.append("file", file);
    try {
      await api.post(`/admin/sets/${setId}/assets`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${seg} ${slot} gespeichert`);
      onDone();
    } catch { toast.error("Upload fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  return (
    <div className="relative rounded-lg overflow-hidden border border-stone-700 aspect-square bg-canvas cursor-pointer group" onClick={() => ref.current?.click()} data-testid={`asset-slot-${seg}-${slot}`}>
      {asset?.asset_url ? (
        <img src={resolveUrl(asset.asset_url)} alt={asset.name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-stone-600"><ImageIcon size={22} /></div>
      )}
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <Upload size={18} className="text-brass" />
      </div>
      <span className="absolute top-1 left-1 font-mono-meta text-[9px] bg-black/60 text-brass px-1 rounded">{slot}</span>
      {busy && <div className="absolute inset-0 bg-black/70 flex items-center justify-center text-brass text-xs">…</div>}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
}

function MatrixTab({ set }) {
  const [sel, setSel] = useState({ top: 1, middle: 1, bottom: 1 });
  const [state, setState] = useState(null);
  const [combinationStates, setCombinationStates] = useState([]);
  const [photo, setPhoto] = useState({ title: "", description: "", completed_at: "" });
  const fileRef = useRef();

  const load = useCallback(async () => {
    const [current, allStates] = await Promise.all([
      api.get(`/admin/sets/${set.id}/combination`, { params: sel }),
      api.get(`/admin/sets/${set.id}/combinations`),
    ]);
    setState(current.data);
    setCombinationStates(allStates.data);
  }, [set.id, sel]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    await api.put(`/admin/sets/${set.id}/combination`, { ...sel, status });
    toast.success(`Status: ${status}`);
    load();
  };
  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    Object.entries(sel).forEach(([k, v]) => fd.append(k, v));
    fd.append("title", photo.title);
    fd.append("description", photo.description);
    fd.append("completed_at", photo.completed_at);
    fd.append("file", file);
    await api.post(`/admin/sets/${set.id}/combination/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Foto hinterlegt, Status = TATTOOED");
    load();
  };

  const Picker = ({ k }) => (
    <div>
      <Label className="text-stone-400 text-xs uppercase">{k === "top" ? "Oberteil" : k === "middle" ? "Mittelteil" : "Unterteil"}</Label>
      <Select value={String(sel[k])} onValueChange={(v) => setSel({ ...sel, [k]: parseInt(v) })}>
        <SelectTrigger data-testid={`matrix-select-${k}`} className="bg-canvas border-stone-700 mt-1"><SelectValue /></SelectTrigger>
        <SelectContent>{Array.from({ length: 10 }).map((_, i) => <SelectItem key={i} value={String(i + 1)}>{i + 1}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="bg-surface border border-stone-800 rounded-xl p-4">
        <div className="grid grid-cols-3 gap-3"><Picker k="top" /><Picker k="middle" /><Picker k="bottom" /></div>
        <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
          <div className="font-mono-meta text-xs text-stone-400">
            Nr. {String(state?.number || 0).padStart(3, "0")} · aktueller Status:{" "}
            <span className="text-brass" data-testid="matrix-current-status">{state?.status || "FREE"}</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {STATUS_OPTS.map((st) => (
              <Button key={st} size="sm" data-testid={`matrix-set-${st}`} variant={state?.status === st ? "default" : "outline"}
                className={state?.status === st ? "cta-brass rounded-full" : "border-stone-700 text-stone-300 rounded-full"}
                onClick={() => setStatus(st)}>{st}</Button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-surface border border-stone-800 rounded-xl p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <h3 className="font-cormorant text-lg text-brass">Reservierte und vergebene Kombinationen</h3>
            <p className="text-xs text-stone-400 mt-1">Alle Kombinationen, die nicht mehr frei sind. Der Status kann hier direkt geändert werden.</p>
          </div>
          <span className="font-mono-meta text-[10px] text-stone-500">{combinationStates.length} Einträge</span>
        </div>
        {!combinationStates.length ? (
          <p className="text-stone-500 text-sm py-5 text-center">Noch keine reservierten oder vergebenen Kombinationen.</p>
        ) : (
          <div className="space-y-2">
            {combinationStates.map((combo) => (
              <div key={combo.combination_key} className="border border-stone-800 bg-canvas rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <strong className="font-cormorant text-lg">Nr. {String(combo.number).padStart(3, "0")}</strong>
                  <div className="font-mono-meta text-[10px] text-stone-500">
                    Kopf {combo.top} · Mitte {combo.middle} · Unten {combo.bottom}
                  </div>
                </div>
                <Select
                  value={combo.status}
                  onValueChange={async (status) => {
                    await api.put(`/admin/sets/${set.id}/combination`, {
                      top: combo.top,
                      middle: combo.middle,
                      bottom: combo.bottom,
                      status,
                    });
                    toast.success(status === "FREE" ? "Kombination wieder freigegeben" : `Status: ${status}`);
                    load();
                  }}
                >
                  <SelectTrigger className="bg-surface border-stone-700 w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FREE">Frei</SelectItem>
                    <SelectItem value="RESERVED">Reserviert</SelectItem>
                    <SelectItem value="TATTOOED">Gestochen</SelectItem>
                    <SelectItem value="DISABLED">Gesperrt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-surface border border-stone-800 rounded-xl p-4">
        <h3 className="font-cormorant text-lg text-brass mb-3">Gestochenes Tattoo (Foto)</h3>
        {state?.completed_image_url && (
          <img src={resolveUrl(state.completed_image_url)} alt="Tattoo" className="w-32 rounded-lg border border-stone-700 mb-3" />
        )}
        <div className="grid sm:grid-cols-3 gap-2 mb-3">
          <Input placeholder="Titel" value={photo.title} onChange={(e) => setPhoto({ ...photo, title: e.target.value })} className="bg-canvas border-stone-700 text-sm" />
          <Input placeholder="Datum" value={photo.completed_at} onChange={(e) => setPhoto({ ...photo, completed_at: e.target.value })} className="bg-canvas border-stone-700 text-sm" />
          <Input placeholder="Beschreibung" value={photo.description} onChange={(e) => setPhoto({ ...photo, description: e.target.value })} className="bg-canvas border-stone-700 text-sm" />
        </div>
        <Button className="cta-brass rounded-full" data-testid="matrix-upload-photo" onClick={() => fileRef.current?.click()}><Upload size={14} className="mr-1" /> Foto hochladen (setzt TATTOOED)</Button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
      </div>
    </div>
  );
}

function RequestsTab({ onChanged }) {
  const [reqs, setReqs] = useState([]);
  const [filter, setFilter] = useState("Alle");
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/requests");
    setReqs(data);
    onChanged?.();
  }, [onChanged]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, admin_status) => {
    try {
      await api.put(`/admin/requests/${id}`, { admin_status });
      toast.success("Anfragestatus gespeichert");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anfragestatus konnte nicht gespeichert werden");
    }
  };

  const release = async (request) => {
    const ok = window.confirm(
      `Kombination Nr. ${String(request.number).padStart(3, "0")} wirklich wieder freigeben? Die Anfrage bleibt zur Dokumentation erhalten.`
    );
    if (!ok) return;
    setBusyId(request.id);
    try {
      await api.post(`/admin/requests/${request.id}/release`);
      toast.success("Kombination ist wieder frei");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Zurücksetzen fehlgeschlagen");
    } finally {
      setBusyId("");
    }
  };

  const deleteRequest = async (request) => {
    const number = String(request.number).padStart(3, "0");
    const ok = window.confirm(
      `Anfrage von ${request.name} (Nr. ${number}) wirklich löschen?\n\nDie Anfrage wird dauerhaft entfernt und die Kombination automatisch wieder auf FREI gesetzt.`
    );
    if (!ok) return;
    setBusyId(request.id);
    try {
      await api.delete(`/admin/requests/${request.id}`);
      toast.success("Anfrage gelöscht und Kombination freigegeben");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anfrage konnte nicht gelöscht werden");
    } finally {
      setBusyId("");
    }
  };

  const resendEmail = async (request) => {
    setBusyId(request.id);
    try {
      const { data } = await api.post(`/admin/requests/${request.id}/resend-email`);
      if (data.email_sent && data.customer_email_sent) {
        toast.success("Admin-Mail und Kundenbestätigung wurden gesendet");
      } else {
        toast.success("Fehlende Mail wurde gesendet");
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mail konnte nicht gesendet werden");
      await load();
    } finally {
      setBusyId("");
    }
  };

  const statuses = ["Alle", "Reserviert", "Frei", "Neu", "In Prüfung", "Angenommen", "Erledigt", "Zurückgesetzt"];
  const matchesFilter = (request, status) => {
    if (status === "Alle") return true;
    if (status === "Reserviert") return request.combination_status === "RESERVED";
    if (status === "Frei") return request.combination_status === "FREE";
    return request.admin_status === status;
  };
  const visible = reqs.filter((request) => matchesFilter(request, filter));
  const count = (status) => reqs.filter((request) => matchesFilter(request, status)).length;

  return (
    <div className="space-y-4" data-testid="requests-list">
      <div className="bg-surface border border-stone-800 rounded-xl p-3 flex items-center gap-2 flex-wrap">
        <span className="text-xs text-stone-400 mr-1">Filtern:</span>
        {statuses.map((status) => (
          <Button
            key={status}
            size="sm"
            variant={filter === status ? "default" : "outline"}
            className={filter === status ? "cta-brass rounded-full" : "border-stone-700 text-stone-300 rounded-full"}
            onClick={() => setFilter(status)}
          >
            {status} ({count(status)})
          </Button>
        ))}
      </div>

      {!visible.length ? (
        <p className="text-stone-500 text-sm py-8 text-center">Keine Anfragen mit diesem Status.</p>
      ) : visible.map((r) => (
        <div key={r.id} className="bg-surface border border-stone-800 rounded-xl p-4" data-testid={`request-row-${r.id}`}>
          <div className="flex justify-between items-start gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-cormorant text-lg">
                {r.name}
                <span className="font-mono-meta text-[10px] text-brass ml-2">Nr. {String(r.number).padStart(3, "0")}</span>
              </div>
              <div className="text-xs text-stone-400 mt-0.5">{r.email}{r.phone ? ` · ${r.phone}` : ""}</div>
              <div className="text-xs text-stone-400">{r.placement} · {r.desired_size}</div>
              {r.message && <div className="text-sm text-stone-300 mt-1 italic">„{r.message}“</div>}
              <div className="flex gap-2 flex-wrap mt-2">
                <span className={`font-mono-meta text-[10px] border rounded-full px-2 py-1 ${r.combination_status === "FREE" ? "text-emerald-400 border-emerald-800" : "text-amber-300 border-amber-800"}`}>
                  Kombination: {r.combination_status === "FREE" ? "FREI" : r.combination_status}
                </span>
                <span className={`font-mono-meta text-[10px] border rounded-full px-2 py-1 ${r.email_sent ? "text-emerald-400 border-emerald-800" : "text-red-400 border-red-900"}`}>
                  Admin-Mail: {r.email_sent ? "GESENDET" : "OFFEN"}
                </span>
                <span className={`font-mono-meta text-[10px] border rounded-full px-2 py-1 ${r.customer_email_sent ? "text-emerald-400 border-emerald-800" : "text-red-400 border-red-900"}`}>
                  Kunden-Mail: {r.customer_email_sent ? "GESENDET" : "OFFEN"}
                </span>
              </div>
              <div className="font-mono-meta text-[10px] text-stone-600 mt-2">{new Date(r.created_at).toLocaleString("de-DE")}</div>
            </div>

            <div className="flex flex-col gap-2 w-full sm:w-auto">
              <Select value={r.admin_status} onValueChange={(v) => setStatus(r.id, v)}>
                <SelectTrigger className="bg-canvas border-stone-700 w-full sm:w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Neu", "In Prüfung", "Angenommen", "Erledigt", "Zurückgesetzt"].map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {(!r.email_sent || !r.customer_email_sent) && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busyId === r.id}
                  className="border-stone-700 text-stone-300"
                  onClick={() => resendEmail(r)}
                >
                  <Mail size={14} className="mr-1" /> {busyId === r.id ? "Wird gesendet…" : "Mail senden"}
                </Button>
              )}

              {r.combination_status !== "FREE" && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busyId === r.id}
                  className="border-red-900 text-red-400 hover:text-red-300"
                  onClick={() => release(r)}
                >
                  <RotateCcw size={14} className="mr-1" /> Kombination freigeben
                </Button>
              )}

              <Button
                size="sm"
                variant="outline"
                disabled={busyId === r.id}
                className="border-red-900 bg-red-950/20 text-red-400 hover:bg-red-950/40 hover:text-red-300"
                onClick={() => deleteRequest(r)}
              >
                <Trash2 size={14} className="mr-1" /> Anfrage löschen
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
